import React from 'react';
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  nanoid,
} from '@reduxjs/toolkit';
import { uniqBy } from 'lodash';

import notificationsApiClient, {
  BackofficeAdminNotificationModel,
} from 'api/notificationsApiClient';
import backofficeNotificationApiClient, {
  DirectionEnum,
  EventTypeEnum,
  NChanEventSetIsNewStatus,
  NotificationStreamInfoModel,
  BackofficeNotificationStreamModel,
  NChanEventSetStatusBeforeSelected,
  NChanEventDeleteNotification,
  NChanEventDeleteBefore,
  InstantNotificationStreamModel,
} from 'api/backofficeNotificationApiClient';
import accountApiClient, {
  NotificationConfigModel,
} from 'api/accountApiClient';
import {
  APPROVED_ENTITY_CONTAINER_ID,
  DEFAULT_NOTIFICATIONS_COUNT_LIMIT,
  POPUP_NOTIFICATIONS_CONTAINER_ID,
} from 'utils/toastUtils';
import {
  getParsedNotification,
  getApprovedEntitiesNotificationIds,
  clearPopupNotificationsWaitingQueue,
  closeAllApprovedEntityNotifications,
} from 'utils/notificationUtils';
import { logout } from './authenticationSlice';
import BaseToast from 'components/wrappers/BaseToast';
import adminApiClient, { BOUserNotificationModel } from 'api/adminApiClient';
import PopupNotificationCard from 'features/notification/PopupNotificationCard';
import EntityApprovedInstantNotification from 'features/notification/ApprovedEntityPopupContainer/EntityApprovedInstantNotification';

interface NotificationState {
  approvedEntitiesInstantNotificationIds: number[];
  approvedEntitiesDelayedNotificationIds: number[];
  notificationStreamConfig?: NotificationConfigModel;
  isHeaderNotificationsVisible: boolean;
  notificationInfo?: NotificationStreamInfoModel;
  notifications: BackofficeNotificationStreamModel[];
  hasMore: boolean;
  isInitialDataLoaded: boolean;
  latestRequestId?: string;
  recipients: BOUserNotificationModel[];
  isRecipientsLoading: boolean;
}

const initialState: NotificationState = {
  approvedEntitiesInstantNotificationIds: [],
  approvedEntitiesDelayedNotificationIds: [],
  notificationStreamConfig: undefined,
  isHeaderNotificationsVisible: false,
  notificationInfo: undefined,
  notifications: [],
  hasMore: true,
  isInitialDataLoaded: false,
  latestRequestId: undefined,
  recipients: [],
  isRecipientsLoading: false,
};

const getNotificationRecipients = createAsyncThunk(
  'notification/getNotificationRecipients',
  async () => {
    return adminApiClient.getRegisteredMembersEmailAndFullName();
  },
);

const sendNotification = createAsyncThunk(
  'notification/sendNotification',
  async (data: BackofficeAdminNotificationModel) => {
    return notificationsApiClient.sendBackofficeAdminNotification(data);
  },
);

const getNotificationStreamConfig = createAsyncThunk(
  'notification/getNotificationStreamConfig',
  async () => {
    return accountApiClient.getNotificationStreamConfig();
  },
);

interface NotificationReadStatusRequest {
  notificationId: number;
  isNew: boolean;
  isUserAdmin: boolean;
}
const setNotificationReadStatus = createAsyncThunk(
  'notification/setNotificationReadStatus',
  async (data: NotificationReadStatusRequest) => {
    const { notificationId, isNew, isUserAdmin } = data;

    return backofficeNotificationApiClient.setNotificationStatus(
      notificationId,
      isNew,
      isUserAdmin
    );
  },
);

interface RemoveNotificationOption {
  notificationId: number;
  isUserAdmin: boolean;
}

const removeNotification = createAsyncThunk(
  'notification/removeNotification',
  async ({ notificationId, isUserAdmin }: RemoveNotificationOption) => {
    return backofficeNotificationApiClient.deleteNotificationById(
      notificationId,
      isUserAdmin
    );
  },
);

interface ReadAllNotificationsOptions {
  notificationId: number;
  isUserAdmin: boolean;
}
const readAllNotifications = createAsyncThunk(
  'notification/readAllNotifications',
  async ({ notificationId, isUserAdmin }: ReadAllNotificationsOptions) => {
    return backofficeNotificationApiClient.readNotificationsBefore(
      notificationId,
      isUserAdmin
    );
  },
);
interface ClearAllNotificationsOptions  {
  notificationId:number,
  isUserAdmin:boolean
}

const clearAllNotifications = createAsyncThunk(
  'notification/clearAllNotifications',
  async ({ notificationId, isUserAdmin }: ClearAllNotificationsOptions) => {
    return backofficeNotificationApiClient.deleteNotificationsBefore(
      notificationId,
      isUserAdmin
    );
  },
);

interface NotificationListRequest {
  notificationId: number;
  direction: DirectionEnum;
  isUserAdmin: boolean;
}
const getNotificationsList = createAsyncThunk(
  'notification/getNotificationsList',
  async (data: NotificationListRequest) => {
    const { notificationId, direction, isUserAdmin } = data;

    return backofficeNotificationApiClient.getNotifications(
      notificationId,
      isUserAdmin,
      direction,
    );
  },
);

const getNotification = createAsyncThunk(
  'notification/getNotification',
  async (notificationId: number) => {
    return backofficeNotificationApiClient.getNotification(notificationId);
  },
);

const getNotificationsInfo = createAsyncThunk(
  'notification/getNotificationsInfo',
  async (isUserAdmin:boolean) => {
    return backofficeNotificationApiClient.getNotificationsInfo(isUserAdmin);
  },
);

const getApprovedEntitiesNotifications = createAsyncThunk(
  'notification/getApprovedEntitiesNotifications',
  async () => {
    return backofficeNotificationApiClient.getAllInstantNotifications();
  },
);

const removeApprovedEntitiesNotifications = createAsyncThunk(
  'notification/removeApprovedEntitiesNotifications',
  async (notificationsIds: number[]) => {
    return backofficeNotificationApiClient.clearInstantNotifications(
      notificationsIds,
    );
  },
);

const addNotificationMessageReducer = (
  state: NotificationState,
  action: PayloadAction<string>,
): NotificationState => {
  const { eventType, payload } = getParsedNotification(action.payload);

  switch (eventType) {
    // Update notification info
    case EventTypeEnum.NotifyStreamInfo:
      const { notificationInfo } = state;
      const streamInfoPayload: NotificationStreamInfoModel = payload;

      // Income revision could be less than current
      // in this case this message is considered as outdated
      return !notificationInfo?.revision ||
        notificationInfo.revision < (streamInfoPayload.revision as number)
        ? {
            ...state,
            notificationInfo: streamInfoPayload,
          }
        : state;

    // Add new notification
    case EventTypeEnum.NotifyNewNotification:
      const newNotificationPayload: BackofficeNotificationStreamModel = payload;

      const notificationExists = state.notifications.find(
        ({ notificationId }) =>
          notificationId === newNotificationPayload.notificationId,
      );

      if (!notificationExists) {
        // Display popup notification only if header notifications are hidden
        // and isShowPopup is true (hide popup for assignment / customer approval)
        if (
          !state.isHeaderNotificationsVisible &&
          newNotificationPayload.isShowPopup
        ) {
          // Remove last notification and clear queue if limit reached
          const popupNotificationIds = BaseToast.getAllToastIds(
            POPUP_NOTIFICATIONS_CONTAINER_ID,
          );

          if (
            popupNotificationIds.length >= DEFAULT_NOTIFICATIONS_COUNT_LIMIT
          ) {
            clearPopupNotificationsWaitingQueue();

            const lastPopupNotificationId = popupNotificationIds[0];

            lastPopupNotificationId &&
              BaseToast.dismiss(lastPopupNotificationId);
          }

          // Show new popup notification
          const popupNotificationId = newNotificationPayload.notificationId;
          BaseToast(
            <PopupNotificationCard notification={newNotificationPayload} />,
            {
              toastId: popupNotificationId,
              containerId: POPUP_NOTIFICATIONS_CONTAINER_ID,
            },
          );
        }

        return {
          ...state,
          notifications: [newNotificationPayload, ...state.notifications],
        };
      }

      return state;

    // Change status of specific notification
    case EventTypeEnum.NotifySetIsNewStatus:
      const newStatusPayload: NChanEventSetIsNewStatus = payload;

      const notifications = state.notifications.map((notification) => {
        return notification.notificationId === newStatusPayload.notificationId
          ? {
              ...notification,
              isNew: newStatusPayload.isNew as boolean,
            }
          : notification;
      });

      return {
        ...state,
        notifications,
      };

    // Remove notification from notification list
    case EventTypeEnum.NotifyDelete:
      const removePayload: NChanEventDeleteNotification = payload;

      return {
        ...state,
        notifications: state.notifications.filter(
          ({ notificationId }) =>
            notificationId !== removePayload.notificationId,
        ),
      };

    // Mark all notifications as read after specific notificationId
    case EventTypeEnum.NotifySetIsNewStatusBefore:
      const readAllPayload: NChanEventSetStatusBeforeSelected = payload;

      const readStartPos = state.notifications.findIndex(
        ({ notificationId }) =>
          notificationId === readAllPayload.lastNotificationId,
      );
      const readNotifications = state.notifications
        .slice(readStartPos)
        .map((notification) => ({
          ...notification,
          isNew: readAllPayload.isNew as boolean,
        }));

      return {
        ...state,
        notifications: [
          ...state.notifications.slice(0, readStartPos),
          ...readNotifications,
        ],
      };

    // Clear all notifications after specific notificationId
    case EventTypeEnum.NotifyDeleteBefore:
      const clearAllPayload: NChanEventDeleteBefore = payload;
      const clearStartPos = state.notifications.findIndex(
        ({ notificationId }) =>
          notificationId === clearAllPayload.lastNotificationId,
      );

      return {
        ...state,
        hasMore: false,
        notifications: [...state.notifications.slice(0, clearStartPos)],
      };

    // Show approved entity instant notificaiton
    case EventTypeEnum.NotifyInstantEvent:
      const approvedEntityPayload: InstantNotificationStreamModel = payload;
      const toastId = nanoid();
      BaseToast(
        <EntityApprovedInstantNotification
          toastId={toastId}
          approvedEntity={payload}
        />,
        {
          toastId,
          containerId: APPROVED_ENTITY_CONTAINER_ID,
        },
      );

      return {
        ...state,
        approvedEntitiesInstantNotificationIds: [
          ...state.approvedEntitiesInstantNotificationIds,
          approvedEntityPayload.notificationId,
        ],
      };

    default:
      return state;
  }
};

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    resetHeaderNotifications: (state) => ({
      ...state,
      isHeaderNotificationsVisible: initialState.isHeaderNotificationsVisible,
      notificationInfo: initialState.notificationInfo,
      notifications: initialState.notifications,
      hasMore: initialState.hasMore,
      isInitialDataLoaded: initialState.isInitialDataLoaded,
      latestRequestId: initialState.latestRequestId,
      recipients: initialState.recipients,
    }),
    resetNotificationList: (state) => ({
      ...state,
      notifications: [],
      hasMore: true,
      isInitialDataLoaded: false,
    }),
    addNotificationMessage: addNotificationMessageReducer,
    handleHeaderNotificationsVisible: (
      state,
      action: PayloadAction<boolean>,
    ) => ({
      ...state,
      isHeaderNotificationsVisible: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      closeAllApprovedEntityNotifications();
      Object.assign(state, initialState);
    });
    builder.addCase(getNotificationsList.pending, (state, action) => {
      state.latestRequestId = action.meta.requestId;
    });
    builder.addCase(getNotificationsList.fulfilled, (state, action) => {
      const { meta, payload } = action;

      if (meta.requestId !== state.latestRequestId) return;

      const { info, chunk: notifications, hasNext } = payload;

      const loadedNotifications = notifications || [];
      const newNotifications = uniqBy(
        [...state.notifications, ...loadedNotifications],
        (notification) => notification.notificationId,
      );
      const hasMoreRows = hasNext || false;

      state.notifications = newNotifications;
      state.hasMore = hasMoreRows;
      state.isInitialDataLoaded = true;
      state.notificationInfo = info;
    });
    builder.addCase(getNotificationsList.rejected, (state, action) => {
      state.hasMore = false;
      state.isInitialDataLoaded = true;
    });
    builder.addCase(getNotificationRecipients.fulfilled, (state, action) => {
      state.recipients = action.payload;
      state.isRecipientsLoading = false;
    });
    builder.addCase(getNotificationRecipients.pending, (state, action) => {
      state.isRecipientsLoading = true;
    });
    builder.addCase(getNotificationRecipients.rejected, (state, action) => {
      state.isRecipientsLoading = false;
    });
    builder.addCase(getNotificationsInfo.fulfilled, (state, action) => {
      state.notificationInfo = action.payload;
    });
    builder.addCase(getNotificationStreamConfig.fulfilled, (state, action) => {
      state.notificationStreamConfig = action.payload;
    });
    builder.addCase(
      getApprovedEntitiesNotifications.fulfilled,
      (state, action) => {
        state.approvedEntitiesDelayedNotificationIds =
          getApprovedEntitiesNotificationIds(action.payload);
      },
    );
    builder.addCase(
      removeApprovedEntitiesNotifications.fulfilled,
      (state, action) => {
        // Remove notifications from state
        state.approvedEntitiesDelayedNotificationIds =
          state.approvedEntitiesDelayedNotificationIds.filter(
            (notificationId) => !action.meta.arg.includes(notificationId),
          );
        state.approvedEntitiesInstantNotificationIds =
          state.approvedEntitiesInstantNotificationIds.filter(
            (notificationId) => !action.meta.arg.includes(notificationId),
          );
      },
    );
  },
});

const { reducer, actions } = notificationSlice;
const {
  resetHeaderNotifications,
  resetNotificationList,
  addNotificationMessage,
  handleHeaderNotificationsVisible,
} = actions;

export {
  getNotification,
  sendNotification,
  removeNotification,
  readAllNotifications,
  getNotificationsList,
  getNotificationsInfo,
  clearAllNotifications,
  resetNotificationList,
  addNotificationMessage,
  resetHeaderNotifications,
  setNotificationReadStatus,
  getNotificationRecipients,
  getNotificationStreamConfig,
  getApprovedEntitiesNotifications,
  handleHeaderNotificationsVisible,
  removeApprovedEntitiesNotifications,
};
export default reducer;
